import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { isEqual } from '@Sage/ssm-utils';
import { SectionListCrud } from '@Sage/ssm-web5';
import classnames from 'classnames';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    ConfigActions,
    EntityCrudActions,
    EntityExtraFieldsActions,
    EntityListActions,
    ServerListActions,
} from 'actions';
import DatePicker from 'components/DatePicker';
import CompaniesRow from 'components/selectRows/CompaniesRow';
import { SALESORDERSLINES } from 'constants/Entities';
import Context from 'managers/Context';
import { defineBaseExtraField, getBackendBoolean, groupFieldsForFieldSelector } from 'utils/fm';
import { getSmartFieldProps } from 'utils/fm/fields';
import { getLiteral } from 'utils/getLiteral';
import { isSageImplementation as resolveIsSageImplementation } from 'utils/sage';
import { logEvent } from 'utils/tracking';

import ProductLinesEmptyView from '../../../components/ProductLinesEmptyView';
import CONFIG from '../../../components/SalesOrdersProductLinesTable/TableConfig';
import { getDefaultColumnsWidth } from '../../../utils';

/** Order lines: valores calculados en OrdersManager; deben mostrarse como texto (currencyCell), no como InputCell readonly (SectionListCrud transforma si el colId está en crudSchema). */
const CALCULATED_CURRENCY_COLUMN_IDS = ['subtotalAmount', 'lineDiscountTotalAmount', 'finalprice'];

const initialState = { groupedFields: null, columnsSizes: null, columnsVisibility: null };

const reducer = (state, action) => {
    switch (action.type) {
        case 'setColumnsSizes':
            return { ...state, columnsSizes: action.columnsSizes };
        case 'setColumnsVisibility':
            return { ...state, columnsVisibility: action.columnsVisibility };
        case 'setState':
            return { ...action.state };
    }
};

const ProductLinesTable = memo(
    ({
        cacheId,
        changeField,
        crudSchema,
        data,
        disableEditSalesOrdersLines,
        errors,
        handleOnRef,
        handleSetCheckedProducts,
        idCurrency,
        idEnvironment,
        isNewEntity,
        parentConfigMap,
        parentData,
    }) => {
        // --- Redux
        const dispatch = useDispatch();

        const {
            cache,
            canDeleteOrderLines,
            isSageImplementation,
            salesOrderAmountDiscounts,
            syncStatusEnabled,
            useTaxesSalesOrders,
        } = useSelector((state) => {
            const oldCache = state?.config?.components?.[`${SALESORDERSLINES.entity}_list`] || null;
            const newCache = (cacheId && state?.config?.components?.[cacheId]) || null;
            const permission = state.config.permission;

            return {
                cache: newCache || oldCache,
                canDeleteOrderLines: permission.crud_permission[SALESORDERSLINES.permission].delete,
                isSageImplementation: resolveIsSageImplementation(state.config?.productModeCode),
                salesOrderAmountDiscounts: !!state?.config?.userData?.salesOrderAmountDiscounts,
                syncStatusEnabled: getBackendBoolean(
                    state?.config?.userData?.syncStatusEnabled || false,
                ),
                useTaxesSalesOrders: getBackendBoolean(
                    state?.config?.userData?.useTaxesSalesOrders || false,
                ),
            };
        }, shallowEqual);

        const {
            getExtraFieldSchema,
            getList,
            setConfigWeb,
            standardFieldsConfiguration,
            updateTableErrors,
        } = useMemo(
            () => ({
                getExtraFieldSchema: bindActionCreators(EntityExtraFieldsActions, dispatch)
                    .getExtraFieldSchema,
                getList: bindActionCreators(ServerListActions, dispatch).getList,
                setConfigWeb: bindActionCreators(ConfigActions, dispatch).setConfigWeb,
                standardFieldsConfiguration: bindActionCreators(EntityListActions, dispatch)
                    .standardFieldsConfiguration,
                updateTableErrors: bindActionCreators(EntityCrudActions, dispatch)
                    .updateTableErrors,
            }),
            [dispatch],
        );

        // --- State
        const [refreshColumnDefs, setRefreshColumnDefs] = useState(false);
        const [state, dispatchLocal] = useReducer(reducer, initialState);
        const [showModalFields, setShowModalFields] = useState(false);
        const errorsRef = useRef(errors);
        const mappedFields = useRef(null);
        const parentDataRef = useRef(parentData);

        // --- Derived state
        const { finalData, finalCrudSchema, schemaHiddenMap } = useMemo(() => {
            const updatedData = data?.map((row) => ({ ...row })) || null;

            const lineSmartFields = crudSchema.filter(
                (f) => f.smartField?.parentEntity === 'SALESORDERSLINES',
            );

            const finalCrudSchema = crudSchema.map((field) => {
                const isLineSmartField = field.smartField?.parentEntity === 'SALESORDERSLINES';

                const smartFieldOverrides =
                    field.smartField && !isLineSmartField
                        ? getSmartFieldProps({
                              field,
                              configMap: parentConfigMap,
                              data: parentData,
                              isNewEntity,
                          })
                        : {};

                const { defaultValue, ...otherOverrides } = smartFieldOverrides;

                if (!!Object.entries(otherOverrides).length && !!defaultValue) {
                    const fieldId = field.isExtra ? field.id.toLowerCase() : field.id;
                    updatedData?.forEach((row) => (row[fieldId] = defaultValue));
                }

                return { ...field, ...otherOverrides };
            });

            if (lineSmartFields.length > 0 && updatedData) {
                const lineConfigMap = crudSchema.reduce((map, field) => {
                    const configKey = field.fieldConfiguration || field.id;
                    const dataKey = field.isExtra ? field.id.toLowerCase() : field.id;
                    map[configKey] = dataKey;
                    return map;
                }, {});

                updatedData.forEach((row) => {
                    const overrides = {};
                    lineSmartFields.forEach((field) => {
                        const props = getSmartFieldProps({
                            field,
                            configMap: lineConfigMap,
                            data: row,
                            isNewEntity: row.isNew ?? isNewEntity,
                        });
                        if (Object.keys(props).length > 0) {
                            const { defaultValue: sfDefault, ...otherProps } = props;
                            const fieldId = field.isExtra ? field.id.toLowerCase() : field.id;
                            overrides[fieldId] = otherProps;
                            const discountTierMatch = /^discount([1-4])$/.exec(fieldId);
                            if (discountTierMatch) {
                                const valueFieldId = `${fieldId}Value`;
                                overrides[valueFieldId] = {
                                    ...(overrides[valueFieldId] || {}),
                                    ...otherProps,
                                };
                            }
                        }
                    });
                    if (Object.keys(overrides).length > 0) {
                        row._lineSmartFieldOverrides = overrides;
                    }
                });
            }

            // Create a map to quickly check if a field is hidden by fieldConfiguration or id
            const schemaHiddenMap = finalCrudSchema.reduce((map, field) => {
                const fieldKey = field.fieldConfiguration || field.id;
                const isHidden = field.hidden === true;
                if (fieldKey) {
                    map[fieldKey] = isHidden;
                    // Also map by id for extra fields
                    if (field.isExtra && field.id) {
                        map[field.id.toLowerCase()] = isHidden;
                    }
                }
                return map;
            }, {});

            return { finalData: updatedData, finalCrudSchema, schemaHiddenMap };
        }, [data, parentData, parentConfigMap, isNewEntity, crudSchema]);

        // --- Effects
        useEffect(() => {
            errorsRef.current = errors;
        }, [errors]);

        useEffect(() => {
            if (!isEqual(parentDataRef.current, parentData)) {
                setRefreshColumnDefs(true);
                parentDataRef.current = parentData;
            } else {
                setRefreshColumnDefs(false);
            }
        }, [parentData]);

        // --- Handlers & column configuration
        const tableParams = useMemo(() => {
            let newTableParams = { numLine: { isCrud: true } };

            if (!canDeleteOrderLines) return newTableParams;

            newTableParams.numLine.onClickCheckbox = (result) => {
                const { checked } = result;
                const checkedRows = checked.numLine.rows;
                const newCheckedRows = Object.keys(checkedRows).filter((key) => {
                    return checkedRows[key];
                });
                handleSetCheckedProducts(newCheckedRows);
            };

            return newTableParams;
        }, [canDeleteOrderLines, handleSetCheckedProducts]);

        const saveCache = useCallback(
            (newObject) => {
                const fieldsKeys = ['visibles', 'sizes'];
                const columnsConfig = fieldsKeys.reduce((obj, field) => {
                    obj[field] = newObject[field];
                    return obj;
                }, {});
                setConfigWeb(`${SALESORDERSLINES.entity}_list`, columnsConfig);
            },
            [setConfigWeb],
        );

        const getCacheConfiguration = useCallback(
            (columnsDef) => {
                const mapped = columnsDef.map((column) => {
                    // Mandatory fields should never be hidden - check first to avoid unnecessary operations
                    const isMandatory = column?.cellRendererParams?.inputParams?.isMandatory;
                    const col = { ...column };
                    if (col.alwaysHidden) {
                        col.hide = true;
                    } else if (isMandatory) {
                        col.hide = false;
                    } else {
                        // Check if column should be hidden based on schema (smart fields)
                        const columnField = col.field || col.colId;
                        const isHiddenBySchema =
                            columnField &&
                            (schemaHiddenMap[columnField] ||
                                schemaHiddenMap[columnField.toLowerCase()]);

                        // Priority: schema hidden > cache visibles
                        if (isHiddenBySchema) {
                            col.hide = true;
                        } else if (cache?.visibles?.hasOwnProperty(col.colId)) {
                            col.hide = !cache.visibles[col.colId];
                        }
                    }

                    if (cache?.sizes?.[col.colId]) col.width = cache?.sizes[col.colId];

                    return col;
                });

                if (!salesOrderAmountDiscounts) return mapped;

                const vis = cache?.visibles || {};
                return mapped.map((column) => {
                    const tierMatch = /^discount([1-4])Value$/.exec(column.colId);
                    if (!tierMatch) return column;
                    if (column.alwaysHidden) return column;
                    const pctId = `discount${tierMatch[1]}`;
                    if (vis[pctId] !== undefined && vis[column.colId] === undefined) {
                        return { ...column, hide: !vis[pctId] };
                    }
                    return column;
                });
            },
            [cache, schemaHiddenMap, salesOrderAmountDiscounts],
        );

        const entityValueListName = useMemo(
            () => ({
                Productos: 'products',
                coferta: 'offers',
                empresas: 'companies',
                Expedientes: 'opportunities',
                viewUsuariosFullName: 'users',
                viewContactosFullName: 'contacts',
            }),
            [],
        );

        const selectCompanyRow = useCallback((props) => {
            if (!props.data) return null;

            return (
                <CompaniesRow
                    info2={props.data.ExtraInfo}
                    info3={props.data.ExtraInfo3}
                    text={props.data.MatchingInfo}
                    {...props}
                />
            );
        }, []);

        const selectComponents = useCallback(
            ({ list }) => {
                const finalEntity = entityValueListName[list] || list;
                const components = { companies: selectCompanyRow };

                return { Option: components[finalEntity] || null };
            },
            [selectCompanyRow, entityValueListName],
        );

        const fieldSelectorLinkedColumnIds = useMemo(() => {
            if (!salesOrderAmountDiscounts) return undefined;
            const tiers = isSageImplementation ? [1] : [1, 2, 3, 4];
            return tiers.reduce((acc, n) => {
                acc[`discount${n}`] = [`discount${n}Value`];
                return acc;
            }, {});
        }, [salesOrderAmountDiscounts, isSageImplementation]);

        const saveColumnSize = useCallback(
            (colId, size) => {
                const newColumnsSizes = { ...state.columnsSizes, [colId]: size };
                const newColumnsConfigObject = {
                    sizes: newColumnsSizes,
                    visibles: state.columnsVisibility,
                };
                saveCache(newColumnsConfigObject);
                dispatchLocal({ type: 'setColumnsSizes', columnsSizes: newColumnsSizes });
            },
            [state.columnsSizes, state.columnsVisibility, saveCache],
        );

        const saveVisibleColumns = useCallback(
            (newColumns) => {
                const newColumnsVisibility = { ...state.columnsVisibility, ...newColumns };

                const newColumnsConfigObject = {
                    sizes: state.columnsSizes,
                    visibles: newColumnsVisibility,
                };

                saveCache(newColumnsConfigObject);
                dispatchLocal({
                    type: 'setColumnsVisibility',
                    columnsVisibility: newColumnsVisibility,
                });
            },
            [state.columnsSizes, state.columnsVisibility, saveCache],
        );

        const loadOptionsFuzzy = useCallback(
            ({ list }) => {
                return () => {
                    return new Promise((resolve, reject) => {
                        const finalList = entityValueListName[list] || list;

                        Context.domainManager.getFuzzySearch(
                            finalList,
                            finalList,
                            'a',
                            null,
                            null,
                            null,
                            (data) => {
                                const options = data.map((current) => {
                                    return {
                                        ...current,
                                        label: current.MatchingInfo,
                                        value: current.Id,
                                    };
                                });
                                resolve(options);
                            },
                            (error) => {
                                console.error('Error while looking for results');
                                reject(error);
                            },
                        );
                    });
                };
            },
            [entityValueListName],
        );

        const loadOptions = useCallback(
            ({ list }) => {
                return () => {
                    return getList(list);
                };
            },
            [getList],
        );

        const getMappedFields = useCallback((columnDefs) => {
            mappedFields.current = columnDefs.reduce((obj, current) => {
                obj[current.field] = current;
                return obj;
            }, {});
        }, []);

        const onChangeCell = useCallback(
            ({ rowIndex, id, field, value }) => {
                // Calculated fields are derived in OrdersManager and must not be edited directly.
                const calculatedFields = [
                    'finalprice',
                    'finalPrice',
                    'subtotalAmount',
                    'lineDiscountTotalAmount',
                    'totalAmount',
                ];
                if (calculatedFields.includes(field)) return;
                if (field === 'taxRateId' && syncStatusEnabled) return;

                changeField('product')({ rowIndex, id, field, value });
                if (!mappedFields.current) return;
                if (errorsRef?.current && Object.keys(errorsRef.current).length > 0) {
                    const newErrors = { ...errorsRef.current };
                    const colId = mappedFields.current?.[field]?.colId;
                    if (
                        colId &&
                        errorsRef.current?.[colId]?.rows?.[rowIndex]?.error === 'mandatory' &&
                        value
                    ) {
                        delete newErrors[colId].rows[rowIndex];
                        updateTableErrors(newErrors);
                    }
                }
            },
            [changeField, syncStatusEnabled, updateTableErrors],
        );

        const getColumnDefs = useCallback(
            ({ transformTableToCrud }) => {
                return new Promise((resolve, reject) => {
                    const newTableParams = {
                        ...tableParams,
                        data: finalData,
                        disabledCheckboxes: disableEditSalesOrdersLines,
                        salesOrderData: parentDataRef.current,
                        useTaxesSalesOrders,
                        isSageImplementation,
                        salesOrderAmountDiscounts,
                    };
                    let newConfig = CONFIG(newTableParams);
                    // Presentation for calculated amounts: always use TableConfig's currencyCell
                    // params. After standardFields / transformTableToCrud the column may no longer
                    // report cellRenderer === 'currencyCell', which skipped our previous restore.
                    const currencyPresentationParamsByColId = new Map(
                        newConfig.columnDefs
                            .filter(
                                (col) =>
                                    CALCULATED_CURRENCY_COLUMN_IDS.includes(col.colId) &&
                                    col.cellRenderer === 'currencyCell',
                            )
                            .map((col) => [col.colId, col.cellRendererParams]),
                    );

                    getExtraFieldSchema(newConfig.entity, (extraFieldsTabs) => {
                        let extraFields = [];
                        const groupedExtraFields = extraFieldsTabs.map((tab) => {
                            let group = {
                                label:
                                    typeof tab.descripcion !== 'string'
                                        ? 'label_customized_fields'
                                        : tab.descripcion,
                                fields: [],
                            };

                            group.fields = tab.tabFields.map((extraField) => {
                                const extraParams = {
                                    decimalAndIntegerParams: { emptyIfNull: true },
                                    entityName: newConfig.entity.extraFieldName,
                                    contextData: {
                                        ...parentDataRef.current,
                                    },
                                };
                                let newExtraField = defineBaseExtraField(extraField, extraParams);
                                if (newExtraField.cellRenderer === 'dateCell') {
                                    newExtraField.cellRendererParams.CustomCell = DatePicker;
                                }

                                extraFields.push(newExtraField);
                                return newExtraField;
                            });

                            return group;
                        });

                        standardFieldsConfiguration(SALESORDERSLINES, newConfig.columnDefs)
                            .then((columnDefs) => {
                                let newColumnDefs = [...columnDefs, ...extraFields];

                                const groupedFields = [
                                    ...groupFieldsForFieldSelector(newConfig, columnDefs, true),
                                    ...groupedExtraFields,
                                ];

                                if (finalCrudSchema) {
                                    newColumnDefs = transformTableToCrud({
                                        columnDefs: newColumnDefs,
                                        crudSchema: finalCrudSchema,
                                        inputProps: {
                                            onChange: onChangeCell,
                                            loadOptions,
                                            loadOptionsFuzzy,
                                            selectComponents,
                                        },
                                    });

                                    if (salesOrderAmountDiscounts) {
                                        const percentInputParamsByTier = new Map();
                                        newColumnDefs.forEach((column) => {
                                            const percentMatch = /^discount([1-4])$/.exec(
                                                column.colId || '',
                                            );
                                            if (!percentMatch) return;
                                            percentInputParamsByTier.set(
                                                percentMatch[1],
                                                column.cellRendererParams?.inputParams || {},
                                            );
                                        });

                                        newColumnDefs = newColumnDefs.map((column) => {
                                            const valueMatch = /^discount([1-4])Value$/.exec(
                                                column.colId || '',
                                            );
                                            if (!valueMatch) return column;

                                            const sourceInputParams = percentInputParamsByTier.get(
                                                valueMatch[1],
                                            );
                                            if (!sourceInputParams) return column;

                                            const inheritedInputParams = {};
                                            [
                                                'isReadOnly',
                                                'readOnly',
                                                'isMandatory',
                                                'mandatory',
                                                'required',
                                            ].forEach((key) => {
                                                if (sourceInputParams[key] !== undefined) {
                                                    inheritedInputParams[key] =
                                                        sourceInputParams[key];
                                                }
                                            });

                                            if (!Object.keys(inheritedInputParams).length) {
                                                return column;
                                            }

                                            return {
                                                ...column,
                                                cellRendererParams: {
                                                    ...column.cellRendererParams,
                                                    inputParams: {
                                                        ...column.cellRendererParams?.inputParams,
                                                        ...inheritedInputParams,
                                                    },
                                                },
                                            };
                                        });
                                    }

                                    // Tax select in order lines must never be clearable.
                                    // Tax rate is part of the line calculation context and
                                    // should always have a concrete value.
                                    // SectionListCrud overwrites inputParams.isReadOnly from schema only;
                                    // re-apply here so syncStatusEnabled can lock the field.
                                    newColumnDefs = newColumnDefs.map((column) => {
                                        if (column.colId !== 'taxRateId') return column;

                                        const schemaReadOnly =
                                            !!column.cellRendererParams?.inputParams?.isReadOnly;
                                        const taxRateReadOnly =
                                            syncStatusEnabled ||
                                            schemaReadOnly ||
                                            disableEditSalesOrdersLines;

                                        if (taxRateReadOnly) {
                                            return {
                                                ...column,
                                                cellRenderer: 'textCell',
                                                crudType: undefined,
                                                valueGetter: (params) => {
                                                    const d = params.data;
                                                    if (!d) return '';
                                                    if (d.taxRate != null && d.taxRate !== '') {
                                                        return String(d.taxRate);
                                                    }
                                                    const tr = d.taxRateId;
                                                    if (tr && typeof tr === 'object' && tr.label) {
                                                        return String(tr.label);
                                                    }
                                                    return tr != null && tr !== ''
                                                        ? String(tr)
                                                        : '';
                                                },
                                                cellRendererParams: {
                                                    align: 'left',
                                                },
                                            };
                                        }

                                        return {
                                            ...column,
                                            cellRendererParams: {
                                                ...column.cellRendererParams,
                                                inputParams: {
                                                    ...column.cellRendererParams?.inputParams,
                                                    isClearable: false,
                                                    isReadOnly: false,
                                                    readOnly: false,
                                                },
                                            },
                                        };
                                    });

                                    newColumnDefs = newColumnDefs.map((column) => {
                                        if (
                                            !CALCULATED_CURRENCY_COLUMN_IDS.includes(column.colId)
                                        ) {
                                            return column;
                                        }
                                        const presentationParams =
                                            currencyPresentationParamsByColId.get(column.colId);
                                        if (!presentationParams) {
                                            return column;
                                        }
                                        return {
                                            ...column,
                                            cellRenderer: 'currencyCell',
                                            cellRendererParams: presentationParams,
                                        };
                                    });
                                }

                                newColumnDefs = getDefaultColumnsWidth(newColumnDefs);

                                newColumnDefs = getCacheConfiguration(newColumnDefs);

                                dispatchLocal({
                                    type: 'setState',
                                    state: {
                                        groupedFields: groupedFields,
                                        columnsSizes: cache?.sizes || [],
                                        columnsVisibility: cache?.visibles || [],
                                    },
                                });
                                getMappedFields(newColumnDefs);
                                resolve(newColumnDefs);
                            })
                            .catch((error) => {
                                console.error('List configuration error:', error);
                                reject(error);
                            });
                    });
                });
            },
            [
                tableParams,
                getExtraFieldSchema,
                standardFieldsConfiguration,
                getCacheConfiguration,
                finalCrudSchema,
                cache?.sizes,
                cache?.visibles,
                getMappedFields,
                onChangeCell,
                loadOptions,
                loadOptionsFuzzy,
                selectComponents,
                finalData,
                disableEditSalesOrdersLines,
                useTaxesSalesOrders,
                syncStatusEnabled,
                isSageImplementation,
                salesOrderAmountDiscounts,
            ],
        );

        const fieldSelectorProps = useMemo(() => {
            if (!state.groupedFields) return null;
            let actions = [];

            actions.push({ type: 'title', label: getLiteral('label_customize') });

            actions.push({
                label: getLiteral('action_select_visible_columns'),
                icon: 'columns',
                id: 'columns',
                onClick: () => {
                    setShowModalFields(true);
                    logEvent({
                        event: SALESORDERSLINES.trueName,
                        functionality: 'visibleFields',
                        checkDuplicate: true,
                    });
                },
            });

            return {
                title: getLiteral('label_showHideColumns'),
                actions,
                groupedColumns: state.groupedFields,
            };
        }, [state.groupedFields]);

        const modalOptionsProps = useCallback(
            () => ({
                advice: getLiteral('label_visible_fields_explanation'),
                isOpen: showModalFields,
                confirmText: getLiteral('action_save'),
                cancelText: getLiteral('action_cancel'),
                placeholderSearch: getLiteral('label_search_field'),
                title: getLiteral('label_visible_fields'),
                onCancel: setShowModalFields,
            }),
            [showModalFields],
        );

        const onOrderProducts = useCallback(
            (event) => {
                //Here we use a promise because the table expects a promise
                return new Promise((resolve, reject) => {
                    let newProducts = [];
                    event.api.forEachNode((current) => {
                        let newProduct = current.data;
                        newProduct.intOrder = current.rowIndex;
                        newProduct.numLine = current.rowIndex + 1;
                        newProducts.push(newProduct);
                    });

                    changeField('orderedProducts')(newProducts);
                    resolve();
                });
            },
            [changeField],
        );

        const columnsRefreshOnDragEnd = useMemo(() => ['numLine', 'rowNumber'], []);

        // --- Derived (render)
        const { domLayout, extraClassName } = useMemo(() => {
            // 50px each row, 400px of max-height for the table
            // we have only the header, so we have 350px for rows -> 7 rows
            if (!finalData?.length) {
                return {
                    domLayout: 'normal',
                    extraClassName: 'sales-order-product-lines-table__empty',
                };
            } else if (finalData?.length <= 7)
                return { domLayout: 'autoHeight', extraClassName: '' };
            else {
                return { domLayout: 'normal', extraClassName: 'table-with-many-rows' };
            }
        }, [finalData?.length]);

        const getRowNodeId = useCallback((data) => data?.id, []);

        // --- Memoized props
        const sectionListCrudProps = useMemo(
            () => ({
                className: classnames(['sales-order-product-lines-table', extraClassName]),
                columnsRefreshOnDragEnd,
                customEmptyViewComponent: ProductLinesEmptyView,
                disabledCheckboxes: disableEditSalesOrdersLines,
                domLayout,
                errors,
                fieldSelector: true,
                fieldSelectorLinkedColumnIds,
                fieldSelectorProps,
                getColumnDefs,
                getRowNodeId,
                id: 'sales-order-product-lines-table',
                idCurrency,
                idEnvironment,
                modalOptionsProps: modalOptionsProps(),
                onDragEnd: onOrderProducts,
                onRef: handleOnRef,
                rowData: finalData,
                saveColumnSize,
                saveVisibleColumns,
                shouldRefreshColumnDefs: refreshColumnDefs,
                useDragRows: !disableEditSalesOrdersLines,
                useSort: false,
            }),
            [
                columnsRefreshOnDragEnd,
                disableEditSalesOrdersLines,
                domLayout,
                errors,
                extraClassName,
                fieldSelectorLinkedColumnIds,
                fieldSelectorProps,
                finalData,
                getColumnDefs,
                getRowNodeId,
                handleOnRef,
                idCurrency,
                idEnvironment,
                modalOptionsProps,
                onOrderProducts,
                refreshColumnDefs,
                saveColumnSize,
                saveVisibleColumns,
            ],
        );

        // --- Render
        return <SectionListCrud {...sectionListCrudProps} />;
    },
);

ProductLinesTable.displayName = 'ProductLinesTable';

export default ProductLinesTable;
